/**
 * Bộ lọc của danh sách khách hàng được giữ trên URL để quay lại từ trang chi tiết
 * (hoặc F5) vẫn còn nguyên. Trang chi tiết không đọc được state của danh sách nên
 * chuỗi query cuối cùng được lưu kèm vào sessionStorage cho nút quay lại dùng.
 */
export interface CustomerListFilters {
    search: string;
    /** Thẻ phân loại đang chọn ở dãy chip S0..S8 (customer_tag_id) */
    tagId: number | null;
    page: number;
    staffId?: number;
    /** Nhân viên trực page phụ trách (in_charge_staff_id) */
    pageStaffId?: number;
    startDate?: string;
    endDate?: string;
    /** Kiểu mốc thời gian của bộ lọc ngày: date | week | month | year */
    period: string;
    /** Ngày tham chiếu đang chọn trên DatePicker (YYYY-MM-DD) */
    refDate?: string;
    oauthAccountIds: number[];
    channelConnectionIds: number[];
    careStatusIds: number[];
}

export const EMPTY_FILTERS: CustomerListFilters = {
    search: "",
    tagId: null,
    page: 1,
    period: "date",
    oauthAccountIds: [],
    channelConnectionIds: [],
    careStatusIds: [],
};

const toNumberList = (raw: string | null): number[] =>
    (raw || "")
        .split(",")
        .map((v) => Number(v))
        .filter((v) => Number.isFinite(v) && v > 0);

const toOptionalNumber = (raw: string | null): number | undefined => {
    const n = Number(raw);
    return raw && Number.isFinite(n) && n > 0 ? n : undefined;
};

export const parseFilters = (params: URLSearchParams): CustomerListFilters => ({
    search: params.get("q") || "",
    tagId: toOptionalNumber(params.get("tag")) ?? null,
    page: toOptionalNumber(params.get("page")) ?? 1,
    staffId: toOptionalNumber(params.get("staff")),
    pageStaffId: toOptionalNumber(params.get("pstaff")),
    startDate: params.get("from") || undefined,
    endDate: params.get("to") || undefined,
    period: params.get("period") || "date",
    refDate: params.get("at") || undefined,
    oauthAccountIds: toNumberList(params.get("oauth")),
    channelConnectionIds: toNumberList(params.get("conn")),
    careStatusIds: toNumberList(params.get("care")),
});

/** Chỉ ghi vào URL những giá trị khác mặc định, tránh query dài dòng */
export const buildFilterParams = (f: CustomerListFilters): URLSearchParams => {
    const params = new URLSearchParams();
    if (f.search) params.set("q", f.search);
    if (f.tagId) params.set("tag", String(f.tagId));
    if (f.page > 1) params.set("page", String(f.page));
    if (f.staffId) params.set("staff", String(f.staffId));
    if (f.pageStaffId) params.set("pstaff", String(f.pageStaffId));
    if (f.startDate) params.set("from", f.startDate);
    if (f.endDate) params.set("to", f.endDate);
    if (f.period && f.period !== "date") params.set("period", f.period);
    if (f.refDate) params.set("at", f.refDate);
    if (f.oauthAccountIds.length) params.set("oauth", f.oauthAccountIds.join(","));
    if (f.channelConnectionIds.length) params.set("conn", f.channelConnectionIds.join(","));
    if (f.careStatusIds.length) params.set("care", f.careStatusIds.join(","));
    return params;
};

const STORAGE_KEY = "customer_list_query";

export const saveCustomerListQuery = (queryString: string) => {
    try {
        sessionStorage.setItem(STORAGE_KEY, queryString);
    } catch {
        // sessionStorage bị chặn (chế độ riêng tư) thì bỏ qua, chỉ mất khôi phục bộ lọc
    }
};

/** Đường dẫn quay lại danh sách kèm bộ lọc gần nhất */
export const getCustomerListPath = (): string => {
    try {
        const qs = sessionStorage.getItem(STORAGE_KEY);
        return qs ? `/customer?${qs}` : "/customer";
    } catch {
        return "/customer";
    }
};
